import { useFetcher, useLoaderData } from "@remix-run/react";
import { loader } from "~/routes-server/blog.server";
import type { loader as blogPostListLoader } from "../routes-server/resources/blogs.server";
import WvBlogPageBanner from "~/components/molecules/WvBlogPageBanner";
import WvBlogPagePopularBlog from "~/components/molecules/WvBlogPagePopularBlog";
import WvBlogPageNewsItem from "~/components/molecules/WvBlogPageNewsItem";
import WvLink from "~/components/atoms/WvLink";
import { usePrismicLabels } from "~/hooks/usePrismicLabel";
import { useLocalePath } from "@commercekit/remix";
import { asText } from "@prismicio/helpers";
import type { BlogPostList } from "~/lib/prismic";
import { useCallback, useEffect, useRef, useState } from "react";
import { metaHelper } from "~/lib/meta";

export const meta = ({
  data,
}: {
  data?: SerializeFrom<typeof loader>;
}) => {
  const base = metaHelper({ data }) ?? [];
  const schema =
    data?.blogSchema != null
      ? [{ "script:ld+json": data.blogSchema } as const]
      : [];
  return [...base, ...schema];
};
export { headers } from "@commercekit/remix/lib.server";
export { loader, blogPostListLoader };
export { default as ErrorBoundary } from "~/components/atoms/GeneralErrorBoundary";

const BlogPage = () => {
  const { blogPage, blogPostList, popularBlogPosts } =
    useLoaderData<typeof loader>();
  const fetcher = useFetcher<typeof blogPostListLoader>();
  const { $pt } = usePrismicLabels();
  const localePath = useLocalePath();
  const [blogPosts, setBlogPosts] = useState<BlogPostList>(blogPostList);

  const loadRef = useRef<HTMLDivElement>(null);
  const [hasMore, setHasMore] = useState(true);
  const nextPage = useRef(2);

  const loadMore = useCallback(() => {
    if (!hasMore || fetcher.state !== "idle") return;
    const top = loadRef.current?.getBoundingClientRect().top;
    const triggerHeight =
      (window.visualViewport?.height ?? window.innerHeight) * 1.5;
    if (top != null && top < triggerHeight) {
      fetcher.load(`/blog?index&page=${nextPage.current}`);
      nextPage.current += 1;
    }
  }, [hasMore, fetcher]);

  useEffect(() => {
    document.body.addEventListener("scroll", loadMore, { passive: true });
    window.addEventListener("scroll", loadMore, { passive: true });
    return () => {
      document.body.removeEventListener("scroll", loadMore);
      window.removeEventListener("scroll", loadMore);
    };
  }, [loadMore]);

  useEffect(() => {
    loadMore();
  }, [loadMore, blogPosts.results.length]);

  // Merge fetched results and advance the page
  useEffect(() => {
    const data = fetcher.data?.blogPostList;
    if (!data) return;
    if (data.results.length === 0) {
      setHasMore(false);
      return;
    }
    setBlogPosts(({ results, ...rest }: BlogPostList) => ({
      ...rest,
      ...data,
      results: [...results, ...data.results],
    }));
    if (!data.next_page) setHasMore(false);
  }, [fetcher.data?.blogPostList]);

  return (
    <section className="subpage">
      <div className="container">
        <div className="breadcrumbs py-3 mb-3">
          <WvLink
            to={localePath("/")}
            className="news first"
            data-icon-before=""
          >
            {$pt("breadcrumbs_label_home")}
          </WvLink>
          <i data-icon-after=""></i>
          <span className="news active" data-icon-before="">
            <span className=""></span>
            {$pt("breadcrumbs_label_blog")}
          </span>
        </div>

        <div className="row">
          <article className="col content mb-3 mb-lg-5 blogpage px-0 px-lg-3">
            <WvBlogPageBanner
              image={blogPage?.data.banner_image}
              wovar={asText(blogPage?.data.banner_title_wovar) ?? ""}
              blog={asText(blogPage?.data.banner_title_blog) ?? ""}
              subtitle={asText(blogPage?.data.banner_sub_title) ?? ""}
            />
            <section className="blogpage__overview">
              <div className="grid-container">
                <WvBlogPagePopularBlog
                  readMore={asText(blogPage?.data.read_more) ?? ""}
                  popularBlogTitle={
                    asText(blogPage?.data.popular_blog_title) ?? ""
                  }
                  popularBlogPosts={popularBlogPosts}
                />

                {blogPosts?.results.length > 0 &&
                  blogPosts?.results.map((result) => (
                    <WvBlogPageNewsItem key={result?.uid} blogPost={result} />
                  ))}
                <div className="loader-container" ref={loadRef}>
                  {fetcher.state === "loading" && (
                    <div className="lds-ellipsis">
                      <div></div>
                      <div></div>
                      <div></div>
                      <div></div>
                    </div>
                  )}
                </div>
              </div>
            </section>
          </article>
        </div>
      </div>
    </section>
  );
};
export default BlogPage;
